import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import type { ProxyBodySpec, VehicleSpec } from './types';

/**
 * Leichte, prozedurale Fahrzeugmodelle fuer die mobile Darstellung.
 *
 * Die Modelle bleiben absichtlich aus wenigen Grundkoerpern aufgebaut. Im
 * Gegensatz zu den alten Platzhalter-Boxen besitzen sie aber eine lesbare
 * Fahrzeugsilhouette, einzeln gefasste Scheiben und typische Details der
 * jeweiligen Fahrzeugklasse. Die oeffentliche Schnittstelle entspricht der
 * des spaeteren GLB-Loaders (Group + gemeinsames Bremslicht-Material).
 */

export interface ProxyBodyResult {
  body: THREE.Group;
  brakeLights: THREE.MeshStandardMaterial;
}

const glassMaterial = new THREE.MeshPhysicalMaterial({
  color: 0x142736,
  metalness: 0.18,
  roughness: 0.16,
  clearcoat: 0.75,
  clearcoatRoughness: 0.12,
  envMapIntensity: 1.8,
});

const trimMaterial = new THREE.MeshStandardMaterial({
  color: 0x171a1d,
  metalness: 0.28,
  roughness: 0.62,
});

const grilleMaterial = new THREE.MeshStandardMaterial({
  color: 0x080a0c,
  metalness: 0.4,
  roughness: 0.48,
});

const steelMaterial = new THREE.MeshStandardMaterial({
  color: 0xaeb6bd,
  metalness: 0.82,
  roughness: 0.3,
});

const headlightMaterial = new THREE.MeshStandardMaterial({
  color: 0xfff7df,
  emissive: 0xffe9b5,
  emissiveIntensity: 1.55,
  roughness: 0.18,
  metalness: 0,
});

const indicatorMaterial = new THREE.MeshStandardMaterial({
  color: 0xff9d19,
  emissive: 0xff7400,
  emissiveIntensity: 0.65,
  roughness: 0.25,
});

const reverseLightMaterial = new THREE.MeshStandardMaterial({
  color: 0xe8f3f7,
  emissive: 0xb8e5ff,
  emissiveIntensity: 0.45,
  roughness: 0.25,
});

const plateMaterial = new THREE.MeshStandardMaterial({
  color: 0xe8e5d9,
  metalness: 0,
  roughness: 0.55,
});

const workLightMaterial = new THREE.MeshStandardMaterial({
  color: 0xfff2c7,
  emissive: 0xffdc83,
  emissiveIntensity: 1.25,
  roughness: 0.2,
});

const Y_AXIS = new THREE.Vector3(0, 1, 0);

export function buildProxyBody(spec: VehicleSpec, paintColor: number): ProxyBodyResult {
  const group = new THREE.Group();
  group.name = 'proxyBody';

  const paint = new THREE.MeshStandardMaterial({
    color: paintColor,
    metalness: spec.category === 'construction' ? 0.32 : 0.58,
    roughness: spec.category === 'construction' ? 0.38 : 0.22,
    envMapIntensity: 1.25,
  });
  const paintDark = paint.clone();
  paintDark.color.multiplyScalar(0.72);
  paintDark.roughness = Math.min(1, paint.roughness + 0.16);

  const brakeLights = new THREE.MeshStandardMaterial({
    color: 0x95160e,
    emissive: 0xff210f,
    emissiveIntensity: 0.35,
    roughness: 0.28,
    metalness: 0,
  });

  if (spec.proxy.kind === 'excavator') {
    buildExcavator(group, spec, paint, paintDark, brakeLights);
  } else if (spec.proxy.kind === 'dumper') {
    buildDumper(group, spec, paint, paintDark, brakeLights);
  } else {
    buildPassengerCar(group, spec, paint, paintDark, brakeLights);
  }

  return { body: group, brakeLights };
}

function buildPassengerCar(
  group: THREE.Group,
  spec: VehicleSpec,
  paint: THREE.MeshStandardMaterial,
  paintDark: THREE.MeshStandardMaterial,
  brakeLights: THREE.MeshStandardMaterial,
): void {
  const p = spec.proxy;
  const lower = p.lower;
  const cabin = p.cabin;
  const bodyTop = lower.y + lower.h * 0.5;
  const bodyBottom = lower.y - lower.h * 0.5;

  addRoundedBox(
    group,
    [lower.w, lower.h * 0.8, lower.l],
    [0, lower.y - lower.h * 0.06, 0],
    paint,
    lower.h * 0.2,
  );

  // Die Schulterlinie nimmt den Quader-Eindruck und verengt Front und Heck.
  addRoundedBox(
    group,
    [lower.w * 0.94, lower.h * 0.38, lower.l * 0.84],
    [0, lower.y + lower.h * 0.27, 0],
    paint,
    lower.h * 0.15,
  );

  const cabinFront = cabin.z + cabin.l * 0.5;
  const cabinRear = cabin.z - cabin.l * 0.5;
  const frontEnd = lower.l * 0.5;
  const rearEnd = -lower.l * 0.5;
  const hoodLength = Math.max(0.42, frontEnd - cabinFront);
  const trunkLength = Math.max(0.3, cabinRear - rearEnd);

  addRoundedBox(
    group,
    [lower.w * 0.88, Math.max(0.08, lower.h * 0.15), hoodLength * 0.98],
    [0, bodyTop + lower.h * 0.035, cabinFront + hoodLength * 0.5],
    paint,
    0.045,
  );
  addRoundedBox(
    group,
    [lower.w * 0.9, Math.max(0.075, lower.h * 0.14), trunkLength * 0.94],
    [0, bodyTop + lower.h * 0.02, cabinRear - trunkLength * 0.5],
    paint,
    0.04,
  );

  addCarCabin(group, cabin, paint);
  addWheelArchTrim(group, spec);

  // Schweller und getrennte Stossfaenger geben dem Fahrzeug optisches Gewicht.
  addRoundedBox(
    group,
    [lower.w * 0.98, lower.h * 0.15, lower.l * 0.74],
    [0, bodyBottom + lower.h * 0.06, 0],
    trimMaterial,
    0.035,
  );
  addRoundedBox(
    group,
    [lower.w * 0.86, lower.h * 0.2, 0.14],
    [0, bodyBottom + lower.h * 0.12, frontEnd + 0.02],
    paintDark,
    0.035,
  );
  addRoundedBox(
    group,
    [lower.w * 0.88, lower.h * 0.2, 0.14],
    [0, bodyBottom + lower.h * 0.12, rearEnd - 0.02],
    paintDark,
    0.035,
  );

  addRoadLights(group, p, brakeLights);

  // Kleine, robuste Details, die auch aus der Spielkamera lesbar bleiben.
  addRoundedBox(
    group,
    [lower.w * 0.44, lower.h * 0.2, 0.045],
    [0, lower.y + lower.h * 0.02, frontEnd + 0.072],
    grilleMaterial,
    0.018,
    false,
  );
  addPlate(group, 0, bodyBottom + lower.h * 0.2, frontEnd + 0.099, 1);
  addPlate(group, 0, bodyBottom + lower.h * 0.2, rearEnd - 0.099, -1);
  addDoorHandles(group, cabin, lower.w);

  if (spec.id === 'familien-suv') addRoofRails(group, cabin);
  if (spec.id === 'sportcoupe') addSportDetails(group, p, trimMaterial);
}

function addCarCabin(
  group: THREE.Group,
  cabin: ProxyBodySpec['cabin'],
  paint: THREE.Material,
): void {
  const bottomY = cabin.y - cabin.h * 0.5;
  const windowBottom = bottomY + cabin.h * 0.1;
  const roofY = cabin.y + cabin.h * 0.5;
  const glassTop = roofY - Math.max(0.055, cabin.h * 0.12);
  const frontBaseZ = cabin.z + cabin.l * 0.5;
  const rearBaseZ = cabin.z - cabin.l * 0.5;
  const roofFrontZ = cabin.z + cabin.l * 0.32;
  const roofRearZ = cabin.z - cabin.l * 0.33;
  const glassWidth = cabin.w * 0.91;

  // Ein lackierter Sockel verhindert, dass die Kabine wie ein Glasblock wirkt.
  addRoundedBox(
    group,
    [cabin.w, cabin.h * 0.18, cabin.l * 0.98],
    [0, bottomY + cabin.h * 0.08, cabin.z],
    paint,
    0.04,
  );

  addSlopedPanel(
    group,
    glassWidth,
    [windowBottom, frontBaseZ - 0.01],
    [glassTop, roofFrontZ],
    glassMaterial,
  );
  addSlopedPanel(
    group,
    glassWidth * 0.96,
    [windowBottom, rearBaseZ + 0.01],
    [glassTop, roofRearZ],
    glassMaterial,
  );

  const splitGap = Math.min(0.05, cabin.l * 0.035);
  const midZ = cabin.z;
  const sideX = cabin.w * 0.505;
  const frontWindow: YZPoint[] = [
    [windowBottom, frontBaseZ - 0.045],
    [windowBottom, midZ + splitGap],
    [glassTop, midZ + splitGap],
    [glassTop, roofFrontZ + 0.015],
  ];
  const rearWindow: YZPoint[] = [
    [windowBottom, midZ - splitGap],
    [windowBottom, rearBaseZ + 0.045],
    [glassTop, roofRearZ - 0.015],
    [glassTop, midZ - splitGap],
  ];
  for (const sx of [-1, 1]) {
    addYZPrism(group, frontWindow, 0.028, sx * sideX, glassMaterial, false);
    addYZPrism(group, rearWindow, 0.028, sx * sideX, glassMaterial, false);

    // A-, B- und C-Saeule sind einzeln sichtbar und rahmen die Scheiben.
    addBeamBetween(
      group,
      new THREE.Vector3(sx * sideX, windowBottom, frontBaseZ),
      new THREE.Vector3(sx * sideX, glassTop, roofFrontZ),
      0.052,
      0.052,
      paint,
    );
    addRoundedBox(
      group,
      [0.05, glassTop - windowBottom + 0.035, 0.065],
      [sx * sideX, (glassTop + windowBottom) * 0.5, midZ],
      trimMaterial,
      0.016,
    );
    addBeamBetween(
      group,
      new THREE.Vector3(sx * sideX, windowBottom, rearBaseZ),
      new THREE.Vector3(sx * sideX, glassTop, roofRearZ),
      0.052,
      0.052,
      paint,
    );
  }

  addRoundedBox(
    group,
    [cabin.w * 0.94, Math.max(0.075, cabin.h * 0.14), roofFrontZ - roofRearZ + 0.12],
    [0, roofY - cabin.h * 0.035, (roofFrontZ + roofRearZ) * 0.5],
    paint,
    0.05,
  );

  // Spiegel mit kurzem dunklem Halter.
  for (const sx of [-1, 1]) {
    addRoundedBox(
      group,
      [0.12, 0.1, 0.18],
      [sx * (cabin.w * 0.5 + 0.075), windowBottom + (glassTop - windowBottom) * 0.38, frontBaseZ - 0.16],
      trimMaterial,
      0.035,
      false,
    );
  }
}

function addWheelArchTrim(group: THREE.Group, spec: VehicleSpec): void {
  const p = spec.proxy;
  const archRadius = spec.wheels.radius * 1.07;
  const archCenterY = spec.wheels.connectionY - spec.suspension.restLength * 0.68;
  for (const sx of [-1, 1]) {
    for (const z of [spec.wheels.frontZ, spec.wheels.rearZ]) {
      const arch = new THREE.Mesh(
        new THREE.TorusGeometry(archRadius, 0.035, 5, 16, Math.PI),
        trimMaterial,
      );
      arch.rotation.y = Math.PI / 2;
      arch.position.set(sx * (p.lower.w * 0.5 + 0.016), archCenterY, z);
      arch.castShadow = true;
      group.add(arch);
    }
  }
}

function addRoadLights(
  group: THREE.Group,
  p: ProxyBodySpec,
  brakeLights: THREE.MeshStandardMaterial,
): void {
  const frontZ = p.lower.l * 0.5 + 0.078;
  const rearZ = -p.lower.l * 0.5 - 0.078;
  const lightY = p.lower.y + p.lower.h * 0.18;
  const lightX = p.lower.w * 0.32;
  const lightWidth = Math.min(0.38, p.lower.w * 0.2);
  const lightHeight = Math.max(0.11, p.lower.h * 0.2);

  for (const sx of [-1, 1]) {
    addRoundedBox(
      group,
      [lightWidth, lightHeight, 0.065],
      [sx * lightX, lightY, frontZ],
      headlightMaterial,
      0.025,
      false,
    );
    addRoundedBox(
      group,
      [lightWidth * 0.22, lightHeight * 0.72, 0.069],
      [sx * (lightX + lightWidth * 0.39), lightY, frontZ + 0.004],
      indicatorMaterial,
      0.018,
      false,
    );

    addRoundedBox(
      group,
      [lightWidth, lightHeight * 1.02, 0.065],
      [sx * lightX, lightY, rearZ],
      brakeLights,
      0.025,
      false,
    );
    addRoundedBox(
      group,
      [lightWidth * 0.26, lightHeight * 0.5, 0.069],
      [sx * (lightX - lightWidth * 0.2), lightY, rearZ - 0.004],
      reverseLightMaterial,
      0.015,
      false,
    );
  }
}

function addDoorHandles(group: THREE.Group, cabin: ProxyBodySpec['cabin'], bodyWidth: number): void {
  const handleY = cabin.y - cabin.h * 0.3;
  for (const sx of [-1, 1]) {
    for (const z of [cabin.z + cabin.l * 0.2, cabin.z - cabin.l * 0.28]) {
      addRoundedBox(
        group,
        [0.035, 0.035, Math.min(0.2, cabin.l * 0.12)],
        [sx * (bodyWidth * 0.5 + 0.012), handleY, z],
        trimMaterial,
        0.012,
        false,
      );
    }
  }
}

function addRoofRails(group: THREE.Group, cabin: ProxyBodySpec['cabin']): void {
  for (const sx of [-1, 1]) {
    addRoundedBox(
      group,
      [0.055, 0.06, cabin.l * 0.67],
      [sx * cabin.w * 0.36, cabin.y + cabin.h * 0.57, cabin.z],
      trimMaterial,
      0.02,
    );
  }
}

function addSportDetails(group: THREE.Group, p: ProxyBodySpec, material: THREE.Material): void {
  const rearZ = -p.lower.l * 0.5 + 0.2;
  const wingY = p.lower.y + p.lower.h * 0.84;
  addRoundedBox(group, [p.lower.w * 0.58, 0.055, 0.18], [0, wingY, rearZ], material, 0.02);
  for (const sx of [-1, 1]) {
    addRoundedBox(group, [0.045, 0.2, 0.06], [sx * p.lower.w * 0.2, wingY - 0.09, rearZ], material, 0.015);
  }
  addRoundedBox(
    group,
    [p.lower.w * 0.76, 0.055, 0.2],
    [0, p.lower.y - p.lower.h * 0.43, p.lower.l * 0.5 + 0.035],
    material,
    0.018,
  );
}

function buildExcavator(
  group: THREE.Group,
  spec: VehicleSpec,
  paint: THREE.MeshStandardMaterial,
  paintDark: THREE.MeshStandardMaterial,
  brakeLights: THREE.MeshStandardMaterial,
): void {
  const p = spec.proxy;
  const lower = p.lower;
  const cabin = p.cabin;
  const lowerTop = lower.y + lower.h * 0.5;
  const rearZ = -lower.l * 0.5;

  addRoundedBox(
    group,
    [lower.w * 0.72, lower.h * 0.34, lower.l * 0.9],
    [0, lower.y - lower.h * 0.28, 0],
    trimMaterial,
    0.06,
  );
  addRoundedBox(
    group,
    [lower.w, lower.h * 0.7, lower.l * 0.88],
    [0, lower.y, -0.08],
    paintDark,
    0.13,
  );

  const turntable = new THREE.Mesh(
    new THREE.CylinderGeometry(lower.w * 0.39, lower.w * 0.44, 0.18, 14),
    trimMaterial,
  );
  turntable.position.set(0, lowerTop + 0.04, -0.3);
  turntable.castShadow = true;
  group.add(turntable);

  addRoundedBox(
    group,
    [lower.w * 0.82, 0.52, lower.l * 0.34],
    [0, lowerTop + 0.34, -0.67],
    paint,
    0.16,
  );

  const cabX = -0.29;
  const cabWidth = cabin.w * 0.76;
  const cabFront = cabin.z + cabin.l * 0.5;
  const cabRear = cabin.z - cabin.l * 0.5;
  addRoundedBox(
    group,
    [cabWidth, cabin.h, cabin.l],
    [cabX, cabin.y, cabin.z],
    paint,
    0.09,
  );
  addRoundedBox(
    group,
    [cabWidth * 0.78, cabin.h * 0.56, 0.04],
    [cabX, cabin.y + cabin.h * 0.08, cabFront + 0.022],
    glassMaterial,
    0.025,
    false,
  );
  addRoundedBox(
    group,
    [cabWidth * 0.7, cabin.h * 0.42, 0.04],
    [cabX, cabin.y + cabin.h * 0.1, cabRear - 0.022],
    glassMaterial,
    0.025,
    false,
  );
  const cabSideWindow: YZPoint[] = [
    [cabin.y - cabin.h * 0.24, cabFront - 0.05],
    [cabin.y - cabin.h * 0.24, cabRear + 0.08],
    [cabin.y + cabin.h * 0.39, cabRear + 0.08],
    [cabin.y + cabin.h * 0.39, cabFront - 0.05],
  ];
  for (const sx of [-1, 1]) {
    addYZPrism(group, cabSideWindow, 0.032, cabX + sx * (cabWidth * 0.505), glassMaterial, false);
    addRoundedBox(
      group,
      [0.05, cabin.h * 0.68, 0.065],
      [cabX + sx * cabWidth * 0.51, cabin.y + cabin.h * 0.07, cabin.z + cabin.l * 0.05],
      trimMaterial,
      0.016,
    );
  }
  addRoundedBox(
    group,
    [cabWidth * 1.08, 0.1, cabin.l * 1.04],
    [cabX, cabin.y + cabin.h * 0.52, cabin.z],
    paintDark,
    0.035,
  );

  // Zweiteiliger Ausleger mit sichtbarer Hydraulik statt langer Balken.
  const boomX = 0.52;
  const pivot = new THREE.Vector3(boomX, lowerTop + 0.38, 0.12);
  const elbow = new THREE.Vector3(boomX, lowerTop + 1.62, lower.l * 0.34);
  const tip = new THREE.Vector3(boomX, lowerTop + 0.34, lower.l * 0.72);
  addBeamBetween(group, pivot, elbow, 0.31, 0.38, paint);
  addBeamBetween(group, elbow, tip, 0.27, 0.32, paint);

  addCylinderBetween(
    group,
    new THREE.Vector3(boomX + 0.2, lowerTop + 0.49, -0.04),
    new THREE.Vector3(boomX + 0.2, lowerTop + 1.28, lower.l * 0.28),
    0.075,
    paintDark,
  );
  addCylinderBetween(
    group,
    new THREE.Vector3(boomX + 0.2, lowerTop + 1.27, lower.l * 0.28),
    new THREE.Vector3(boomX + 0.2, lowerTop + 1.54, lower.l * 0.33),
    0.042,
    steelMaterial,
  );
  addCylinderBetween(
    group,
    new THREE.Vector3(boomX - 0.19, lowerTop + 1.45, lower.l * 0.34),
    new THREE.Vector3(boomX - 0.19, lowerTop + 0.63, lower.l * 0.64),
    0.047,
    steelMaterial,
  );

  const bucketPoints: YZPoint[] = [
    [tip.y + 0.17, tip.z - 0.08],
    [tip.y + 0.04, tip.z + 0.48],
    [tip.y - 0.39, tip.z + 0.66],
    [tip.y - 0.5, tip.z + 0.24],
  ];
  addYZPrism(group, bucketPoints, 0.94, boomX, paintDark);
  addRoundedBox(
    group,
    [1.02, 0.085, 0.16],
    [boomX, tip.y - 0.45, tip.z + 0.6],
    steelMaterial,
    0.025,
  );

  // Trittstufe, Arbeitsleuchten und Rueckleuchten.
  addRoundedBox(group, [0.48, 0.08, 0.52], [-0.78, lowerTop + 0.06, 0.34], trimMaterial, 0.025);
  for (const sx of [-1, 1]) {
    addRoundedBox(
      group,
      [0.17, 0.14, 0.08],
      [cabX + sx * cabWidth * 0.32, cabin.y + cabin.h * 0.43, cabFront + 0.085],
      workLightMaterial,
      0.025,
      false,
    );
    addRoundedBox(
      group,
      [0.22, 0.14, 0.07],
      [sx * lower.w * 0.31, lower.y + lower.h * 0.12, rearZ - 0.045],
      brakeLights,
      0.025,
      false,
    );
  }
}

function buildDumper(
  group: THREE.Group,
  spec: VehicleSpec,
  paint: THREE.MeshStandardMaterial,
  paintDark: THREE.MeshStandardMaterial,
  brakeLights: THREE.MeshStandardMaterial,
): void {
  const p = spec.proxy;
  const lower = p.lower;
  const lowerBottom = lower.y - lower.h * 0.5;
  const frontZ = lower.l * 0.5;
  const rearZ = -lower.l * 0.5;

  // Sichtbare Leiterrahmen-Optik und kompakte Motorhaube.
  addRoundedBox(
    group,
    [lower.w * 0.7, lower.h * 0.26, lower.l * 0.91],
    [0, lowerBottom + lower.h * 0.16, 0],
    trimMaterial,
    0.055,
  );
  addRoundedBox(
    group,
    [lower.w * 0.9, lower.h * 0.46, lower.l * 0.34],
    [0, lower.y, lower.l * 0.32],
    paintDark,
    0.1,
  );

  buildTruckCab(group, p, paint, paintDark);
  addDumperBed(group, p, paint, paintDark);

  addRoundedBox(
    group,
    [lower.w * 0.92, 0.2, 0.16],
    [0, lowerBottom + 0.13, frontZ + 0.03],
    trimMaterial,
    0.04,
  );
  addRoundedBox(
    group,
    [lower.w * 0.87, 0.18, 0.15],
    [0, lowerBottom + 0.14, rearZ - 0.03],
    trimMaterial,
    0.04,
  );

  const lightX = lower.w * 0.33;
  for (const sx of [-1, 1]) {
    addRoundedBox(
      group,
      [0.3, 0.22, 0.075],
      [sx * lightX, lower.y + 0.08, frontZ + 0.08],
      headlightMaterial,
      0.035,
      false,
    );
    addRoundedBox(
      group,
      [0.27, 0.2, 0.075],
      [sx * lightX, lower.y, rearZ - 0.08],
      brakeLights,
      0.035,
      false,
    );
  }
  addPlate(group, 0, lowerBottom + 0.27, frontZ + 0.113, 1);
  addPlate(group, 0, lowerBottom + 0.27, rearZ - 0.113, -1);

  // Breite Kotfluegel machen die grossen Raeder optisch Teil des Aufbaus.
  const archY = spec.wheels.connectionY - spec.suspension.restLength * 0.63;
  for (const sx of [-1, 1]) {
    for (const z of [spec.wheels.frontZ, spec.wheels.rearZ]) {
      const arch = new THREE.Mesh(
        new THREE.TorusGeometry(spec.wheels.radius * 1.08, 0.055, 5, 16, Math.PI),
        paintDark,
      );
      arch.rotation.y = Math.PI / 2;
      arch.position.set(sx * (lower.w * 0.5 + 0.025), archY, z);
      arch.castShadow = true;
      group.add(arch);
    }
  }
}

function buildTruckCab(
  group: THREE.Group,
  p: ProxyBodySpec,
  paint: THREE.Material,
  paintDark: THREE.Material,
): void {
  const c = p.cabin;
  const front = c.z + c.l * 0.5;
  const rear = c.z - c.l * 0.5;
  addRoundedBox(group, [c.w, c.h, c.l], [0, c.y, c.z], paint, 0.11);

  addSlopedPanel(
    group,
    c.w * 0.78,
    [c.y - c.h * 0.08, front + 0.025],
    [c.y + c.h * 0.37, front - c.l * 0.07],
    glassMaterial,
  );
  const sideWindow: YZPoint[] = [
    [c.y - c.h * 0.12, front - 0.08],
    [c.y - c.h * 0.12, rear + c.l * 0.18],
    [c.y + c.h * 0.35, rear + c.l * 0.18],
    [c.y + c.h * 0.35, front - c.l * 0.12],
  ];
  for (const sx of [-1, 1]) {
    addYZPrism(group, sideWindow, 0.035, sx * c.w * 0.505, glassMaterial, false);
    addRoundedBox(
      group,
      [0.15, 0.13, 0.22],
      [sx * (c.w * 0.5 + 0.11), c.y + c.h * 0.08, front - c.l * 0.12],
      trimMaterial,
      0.035,
      false,
    );
    addRoundedBox(
      group,
      [0.38, 0.09, c.l * 0.56],
      [sx * (c.w * 0.5 + 0.14), c.y - c.h * 0.53, c.z],
      trimMaterial,
      0.025,
    );
  }
  addRoundedBox(
    group,
    [c.w * 1.03, 0.11, c.l * 1.02],
    [0, c.y + c.h * 0.51, c.z],
    paintDark,
    0.04,
  );
  addRoundedBox(
    group,
    [c.w * 0.55, c.h * 0.24, 0.05],
    [0, c.y - c.h * 0.25, front + 0.04],
    grilleMaterial,
    0.02,
    false,
  );
}

function addDumperBed(
  group: THREE.Group,
  p: ProxyBodySpec,
  paint: THREE.Material,
  dark: THREE.Material,
): void {
  const lower = p.lower;
  const bedWidth = lower.w * 0.98;
  const bedFront = lower.l * 0.11;
  const bedRear = -lower.l * 0.47;
  const baseY = lower.y + lower.h * 0.43;
  const topY = baseY + 0.78;
  const side: YZPoint[] = [
    [baseY, bedFront - 0.12],
    [topY, bedFront],
    [topY, bedRear],
    [baseY, bedRear + 0.24],
  ];

  for (const sx of [-1, 1]) {
    addYZPrism(group, side, 0.13, sx * (bedWidth * 0.5 - 0.06), paint);
    addRoundedBox(
      group,
      [0.1, 0.1, bedFront - bedRear],
      [sx * bedWidth * 0.5, topY + 0.02, (bedFront + bedRear) * 0.5],
      dark,
      0.025,
    );
  }

  addRoundedBox(
    group,
    [bedWidth - 0.18, 0.12, bedFront - bedRear - 0.28],
    [0, baseY + 0.03, (bedFront + bedRear) * 0.5 + 0.04],
    dark,
    0.025,
  );
  addRoundedBox(
    group,
    [bedWidth - 0.12, topY - baseY, 0.13],
    [0, (topY + baseY) * 0.5, bedFront - 0.035],
    paint,
    0.035,
  );
  addRoundedBox(
    group,
    [bedWidth - 0.12, topY - baseY, 0.13],
    [0, (topY + baseY) * 0.5, bedRear + 0.035],
    paint,
    0.035,
  );

  for (const sx of [-0.28, 0, 0.28]) {
    addRoundedBox(
      group,
      [0.075, topY - baseY - 0.16, 0.07],
      [sx * bedWidth, (topY + baseY) * 0.5, bedRear - 0.045],
      dark,
      0.018,
    );
  }

  addCylinderBetween(
    group,
    new THREE.Vector3(0, lower.y + 0.05, -0.15),
    new THREE.Vector3(0, baseY + 0.08, bedFront - 0.34),
    0.075,
    steelMaterial,
  );
}

function addPlate(group: THREE.Group, x: number, y: number, z: number, direction: 1 | -1): void {
  addRoundedBox(group, [0.34, 0.12, 0.025], [x, y, z], plateMaterial, 0.018, false);
  // Dunkle Halterung gibt der hellen Flaeche auch auf sehr hellen Lacken Kontur.
  addRoundedBox(
    group,
    [0.4, 0.16, 0.018],
    [x, y, z - direction * 0.018],
    trimMaterial,
    0.018,
    false,
  );
}

function addSlopedPanel(
  group: THREE.Group,
  width: number,
  bottom: YZPoint,
  top: YZPoint,
  material: THREE.Material,
): THREE.Mesh {
  const dy = top[0] - bottom[0];
  const dz = top[1] - bottom[1];
  const height = Math.hypot(dy, dz);
  const panel = new THREE.Mesh(
    new RoundedBoxGeometry(width, height, 0.038, 2, 0.016),
    material,
  );
  panel.position.set(0, (bottom[0] + top[0]) * 0.5, (bottom[1] + top[1]) * 0.5);
  panel.rotation.x = Math.atan2(dz, dy);
  group.add(panel);
  return panel;
}

function addRoundedBox(
  group: THREE.Group,
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  material: THREE.Material,
  radius = 0.06,
  castShadow = true,
): THREE.Mesh {
  const safeRadius = Math.max(0.006, Math.min(radius, size[0] * 0.45, size[1] * 0.45, size[2] * 0.45));
  const mesh = new THREE.Mesh(
    new RoundedBoxGeometry(size[0], size[1], size[2], 2, safeRadius),
    material,
  );
  mesh.position.set(position[0], position[1], position[2]);
  mesh.castShadow = castShadow;
  group.add(mesh);
  return mesh;
}

function addBeamBetween(
  group: THREE.Group,
  start: THREE.Vector3,
  end: THREE.Vector3,
  width: number,
  depth: number,
  material: THREE.Material,
): THREE.Mesh {
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  const beam = new THREE.Mesh(
    new RoundedBoxGeometry(width, length, depth, 2, Math.min(0.055, width * 0.3, depth * 0.3)),
    material,
  );
  beam.position.copy(start).add(end).multiplyScalar(0.5);
  beam.quaternion.setFromUnitVectors(Y_AXIS, direction.normalize());
  beam.castShadow = true;
  group.add(beam);
  return beam;
}

function addCylinderBetween(
  group: THREE.Group,
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
  material: THREE.Material,
): THREE.Mesh {
  const direction = new THREE.Vector3().subVectors(end, start);
  const cylinder = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, direction.length(), 8),
    material,
  );
  cylinder.position.copy(start).add(end).multiplyScalar(0.5);
  cylinder.quaternion.setFromUnitVectors(Y_AXIS, direction.normalize());
  cylinder.castShadow = true;
  group.add(cylinder);
  return cylinder;
}

type YZPoint = readonly [y: number, z: number];

/** Erzeugt ein leichtes Prisma aus einem konvexen Profil in der Y/Z-Ebene. */
function addYZPrism(
  group: THREE.Group,
  points: readonly YZPoint[],
  width: number,
  x: number,
  material: THREE.Material,
  castShadow = true,
): THREE.Mesh {
  const geometry = createYZPrismGeometry(points, width);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.x = x;
  mesh.castShadow = castShadow;
  group.add(mesh);
  return mesh;
}

function createYZPrismGeometry(points: readonly YZPoint[], width: number): THREE.BufferGeometry {
  const count = points.length;
  const positions: number[] = [];
  const halfWidth = width * 0.5;
  for (const x of [-halfWidth, halfWidth]) {
    for (const [y, z] of points) positions.push(x, y, z);
  }

  const indices: number[] = [];
  let signedArea = 0;
  for (let i = 0; i < count; i++) {
    const current = points[i];
    const next = points[(i + 1) % count];
    if (!current || !next) continue;
    signedArea += current[0] * next[1] - next[0] * current[1];
  }
  const pointsTowardPositiveX = signedArea >= 0;
  for (let i = 1; i < count - 1; i++) {
    if (pointsTowardPositiveX) {
      indices.push(0, i + 1, i);
      indices.push(count, count + i, count + i + 1);
    } else {
      indices.push(0, i, i + 1);
      indices.push(count, count + i + 1, count + i);
    }
  }
  for (let i = 0; i < count; i++) {
    const next = (i + 1) % count;
    if (pointsTowardPositiveX) {
      indices.push(i, next, count + next, i, count + next, count + i);
    } else {
      indices.push(i, count + next, next, i, count + i, count + next);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

let sharedTireGeo: THREE.TorusGeometry | null = null;
let sharedRimGeo: THREE.CylinderGeometry | null = null;
let sharedHubGeo: THREE.CylinderGeometry | null = null;

const tireMaterial = new THREE.MeshStandardMaterial({
  color: 0x101215,
  roughness: 0.96,
  metalness: 0,
});
const rimMaterial = new THREE.MeshStandardMaterial({
  color: 0xc4cbd1,
  roughness: 0.3,
  metalness: 0.86,
  envMapIntensity: 1.35,
});
const hubMaterial = new THREE.MeshStandardMaterial({
  color: 0x343a40,
  roughness: 0.42,
  metalness: 0.72,
});

/** Ein Rad als Gruppe: runder Reifen, Felgenbett und Nabe; Achse entlang X. */
export function buildWheel(radius: number, width: number): THREE.Group {
  if (!sharedTireGeo) {
    sharedTireGeo = new THREE.TorusGeometry(0.72, 0.28, 6, 18);
    sharedTireGeo.rotateY(Math.PI / 2);
  }
  if (!sharedRimGeo) {
    sharedRimGeo = new THREE.CylinderGeometry(1, 1, 1, 16, 1);
    sharedRimGeo.rotateZ(Math.PI / 2);
  }
  if (!sharedHubGeo) {
    sharedHubGeo = new THREE.CylinderGeometry(1, 1, 1, 10, 1);
    sharedHubGeo.rotateZ(Math.PI / 2);
  }

  const wheel = new THREE.Group();

  const tire = new THREE.Mesh(sharedTireGeo, tireMaterial);
  tire.scale.set(width / 0.56, radius, radius);
  tire.castShadow = true;
  wheel.add(tire);

  const rim = new THREE.Mesh(sharedRimGeo, rimMaterial);
  rim.scale.set(width * 0.82, radius * 0.57, radius * 0.57);
  rim.castShadow = true;
  wheel.add(rim);

  const hub = new THREE.Mesh(sharedHubGeo, hubMaterial);
  hub.scale.set(width * 0.89, radius * 0.21, radius * 0.21);
  wheel.add(hub);

  return wheel;
}
